import { loadScript } from '~/utils/commonUtils.ts';
import logger from '~/utils/logger';

type StripeInstance = any;

declare global {
  interface Window {
    Stripe?: (publishableKey: string) => StripeInstance;
  }
}

const STRIPE_JS_SRC = 'https://js.stripe.com/clover/stripe.js';

let stripeInstance: StripeInstance | null = null;
let stripeInitPromise: Promise<StripeInstance> | null = null;
let stripePublishableKey: string | null = null;

async function initStripe(
  publishableKey: string,
  onError: (err: Event | Error) => Promise<void>
): Promise<StripeInstance> {
  if (!publishableKey) {
    throw new Error('Stripe publishable key is required');
  }

  if (stripeInstance) {
    if (stripePublishableKey && stripePublishableKey !== publishableKey) {
      logger.warn(
        'Stripe already initialized with a different publishable key; returning existing instance.'
      );
    }
    return stripeInstance;
  }

  if (stripeInitPromise) {
    return stripeInitPromise;
  }

  stripePublishableKey = publishableKey;

  const initializeInstance = () => {
    if (!window.Stripe) {
      throw new Error('Stripe.js is not available on window');
    }
    stripeInstance = window.Stripe(publishableKey);
  };

  stripeInitPromise = loadScript({
    src: STRIPE_JS_SRC,
    onLoad: async () => {
      initializeInstance();
    },
    onError: async (err) => {
      logger.error('Failed to load Stripe.js script', err);
      await onError(err);
      throw new Error('Failed to load Stripe.js script');
    }
  })
    .then(() => {
      if (!stripeInstance) {
        // Fallback: script was already present, so initialize now.
        initializeInstance();
      }
      return stripeInstance!;
    })
    .catch((err) => {
      stripeInitPromise = null;
      stripePublishableKey = null;
      throw err;
    });

  return stripeInitPromise;
}

export { initStripe };
